import React, { useState } from "react";
import {
  formatDateTime,
  formatPortalName,
  getRelativeTime,
  extractPortalsList,
  extractTotalClicks,
} from "./utils";
import "./WebAnalytics.css";

function WebAnalyticsTable({ data, loading, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState("");

  const portals = extractPortalsList(data);
  const totalClicks = extractTotalClicks(data);

  const filteredPortals = portals.filter((item) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = formatPortalName(item.portalId).toLowerCase();
    const id = (item.portalId || "").toLowerCase();
    return name.includes(term) || id.includes(term);
  });

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-transparent border-bottom pt-3 pb-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="d-flex align-items-center gap-2">
          <div className="bg-primary-subtle p-2 rounded text-primary d-inline-flex">
            <i className="bi bi-bar-chart-line-fill fs-5"></i>
          </div>
          <div>
            <h5 className="card-title fw-bold mb-0">Portal Click Details</h5>
            <small className="text-muted">
              {portals.length} {portals.length === 1 ? "portal" : "portals"} monitored
            </small>
          </div>
        </div>

        {portals.length > 0 && (
          <div className="d-flex align-items-center gap-2" style={{ maxWidth: "300px", width: "100%" }}>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-light border-end-0">
                <i className="bi bi-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control bg-light border-start-0 ps-0"
                placeholder="Filter by portal name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={() => setSearchTerm("")}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th scope="col" className="ps-4" style={{ width: "60px" }}>#</th>
                <th scope="col">Portal Name</th>
                <th scope="col" className="text-center">Click Count</th>
                <th scope="col" style={{ width: "220px" }}>Traffic Share</th>
                <th scope="col">Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                    <span className="text-muted fw-semibold">Loading portal clicks data...</span>
                  </td>
                </tr>
              ) : filteredPortals.length > 0 ? (
                filteredPortals.map((portal, idx) => {
                  const relativeTime = getRelativeTime(portal.lastUpdated);
                  const sharePercentage = totalClicks > 0
                    ? ((Number(portal.count) / totalClicks) * 100).toFixed(1)
                    : 0;

                  return (
                    <tr key={portal.id || idx}>
                      <td className="ps-4 text-muted fw-bold small">{idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold text-dark">
                            {formatPortalName(portal.portalId)}
                          </span>
                          <span className="badge bg-light text-secondary border font-monospace small">
                            {portal.portalId}
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-primary fs-6 px-3 py-2 rounded-pill shadow-sm">
                          <i className="bi bi-cursor-fill me-1"></i>
                          {Number(portal.count).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <div className="d-flex justify-content-between align-items-center small">
                            <span className="text-muted font-monospace">{sharePercentage}%</span>
                          </div>
                          <div className="wa-progress-bar-bg">
                            <div
                              className="wa-progress-bar-fill"
                              style={{ width: `${Math.min(100, sharePercentage)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fw-semibold text-dark font-monospace small">
                            {formatDateTime(portal.lastUpdated)}
                          </span>
                          {relativeTime && (
                            <small className="text-muted wa-table-relative-time">
                              ({relativeTime})
                            </small>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-2 text-secondary d-block mb-2"></i>
                    {searchTerm ? "No portals match your search." : "No click analytics data available."}
                  </td>
                </tr>
              )}
            </tbody>
            {portals.length > 0 && !loading && (
              <tfoot className="table-light border-top">
                <tr>
                  <td colSpan="2" className="ps-4 fw-bold text-dark">
                    Total ({portals.length} Portals)
                  </td>
                  <td className="text-center">
                    <span className="badge bg-dark fs-6 px-3 py-2 rounded-pill">
                      {totalClicks.toLocaleString()} Total Clicks
                    </span>
                  </td>
                  <td colSpan="2" className="text-muted small">
                    100% Total Accumulated Traffic
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

export default WebAnalyticsTable;

